import { useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  Upload,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Sparkles,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/ui/pill';
import { Eyebrow } from '@/components/ui/eyebrow';
import { useAuth } from '@/lib/auth';
import {
  useParseOfx,
  useApplyConciliacao,
  type MatchSuggestion,
} from '@/lib/hooks/conciliacao';
import { fmtBRL, fmtDate } from '@/lib/utils';

export const Route = createFileRoute('/_app/conciliacao')({
  component: ConciliacaoPage,
});

function ConciliacaoPage() {
  const { user } = useAuth();
  const workspaceId = user?.effectiveWorkspaceId ?? null;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [parsed, setParsed] = useState<{ banco: string | null; sugestoes: MatchSuggestion[] } | null>(
    null,
  );
  const [selectedPagamentos, setSelectedPagamentos] = useState<Set<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);

  const parseMut = useParseOfx(workspaceId);
  const applyMut = useApplyConciliacao(workspaceId);

  async function handleFile(file: File) {
    if (!file.name.toLowerCase().endsWith('.ofx') && file.type !== 'application/x-ofx') {
      toast.error('Aceito apenas arquivos OFX por enquanto. CSV/PDF na próxima fase.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Arquivo excede 5 MB.');
      return;
    }
    try {
      const content = await file.text();
      const result = await parseMut.mutateAsync(content);
      setParsed({ banco: result.banco, sugestoes: result.sugestoes });
      setSelectedPagamentos(new Set());
      toast.success(`${result.totalTransacoes} transações lidas.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao processar OFX.');
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function togglePagamento(pagamentoId: string) {
    setSelectedPagamentos((prev) => {
      const next = new Set(prev);
      if (next.has(pagamentoId)) next.delete(pagamentoId);
      else next.add(pagamentoId);
      return next;
    });
  }

  async function applySelected() {
    if (selectedPagamentos.size === 0) {
      toast.error('Nenhum pagamento selecionado.');
      return;
    }
    try {
      const r = await applyMut.mutateAsync(Array.from(selectedPagamentos));
      toast.success(`${r.updated} pagamento(s) conciliados.`);
      setSelectedPagamentos(new Set());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao aplicar conciliação.');
    }
  }

  if (!workspaceId) {
    return (
      <Card variant="default" className="p-8 text-center">
        <p className="text-sm text-fg-muted">Selecione um workspace para conciliar extratos.</p>
      </Card>
    );
  }

  const sugestoes = parsed?.sugestoes ?? [];
  const counts = {
    matched: sugestoes.filter((s) => s.status === 'matched').length,
    centavos: sugestoes.filter((s) => s.status === 'centavos').length,
    ambiguous: sugestoes.filter((s) => s.status === 'ambiguous').length,
    unmatched: sugestoes.filter((s) => s.status === 'unmatched').length,
  };

  // Cálculos de totais para o rodapé e heroes
  // valor é string na API — parseFloat para aritmética
  const totalBanco = sugestoes.reduce((acc, s) => acc + parseFloat(s.transacao.valor), 0);
  const totalSistema = sugestoes.reduce((acc, s) => {
    const candidatoSelecionado = s.candidatos.find((c) => selectedPagamentos.has(c.pagamentoId));
    return acc + (candidatoSelecionado ? parseFloat(candidatoSelecionado.valor) : 0);
  }, 0);
  const diffTotal = totalBanco - totalSistema;

  const hasDivergencias = counts.centavos + counts.ambiguous + counts.unmatched > 0;
  const ctaDisabled = applyMut.isPending || selectedPagamentos.size === 0 || counts.unmatched > 0;

  // Mês de competência: pega a data mais frequente das transações ou mês atual
  const mesCompetencia = (() => {
    if (sugestoes.length === 0) {
      return new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }).toUpperCase();
    }
    const primeiraTx = sugestoes[0]?.transacao.data;
    if (!primeiraTx) return new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }).toUpperCase();
    const d = new Date(primeiraTx);
    return d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }).toUpperCase();
  })();

  const divergenciasCount = counts.centavos + counts.ambiguous + counts.unmatched;

  return (
    <div className="flex flex-col gap-5">
      {/* Header page */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>Bancário</Eyebrow>
          <h1 className="font-display text-[34px] md:text-[42px] font-bold tracking-[-0.03em] text-fg leading-[1.0] mt-1">
            Conciliação
          </h1>
        </div>
        {!parsed && (
          <Button
            variant="default"
            size="lg"
            onClick={() => fileInputRef.current?.click()}
            disabled={parseMut.isPending}
          >
            {parseMut.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                Processando…
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" strokeWidth={1.8} />
                Importar extrato OFX
              </>
            )}
          </Button>
        )}
      </header>

      {/* Hero strip */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Hero mint — sempre visível */}
        <Card variant="mint" className="p-6">
          <span className="font-mono text-[10px] tracking-[0.10em] uppercase font-semibold opacity-70">
            {parsed ? mesCompetencia : 'CONCILIAÇÃO'}
          </span>
          <h2 className="font-display text-[28px] font-bold tracking-[-0.025em] leading-[1.05] mt-2 mb-2">
            {parsed
              ? `${counts.matched + counts.centavos} de ${sugestoes.length} conciliados`
              : `${new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}`}
          </h2>
          {parsed ? (
            <p className="font-mono text-sm opacity-80">
              {fmtBRL(totalSistema)} / {fmtBRL(totalBanco)}
            </p>
          ) : (
            <p className="text-sm opacity-80">
              Cruze o extrato bancário com os pagamentos do sistema — matching por valor + data ± 2 dias.
            </p>
          )}
        </Card>

        {/* Hero ink — só aparece se há divergências após upload */}
        {parsed && hasDivergencias && (
          <Card variant="ink" className="p-6">
            <span className="font-mono text-[10px] tracking-[0.10em] uppercase font-semibold opacity-70">
              ATENÇÃO
            </span>
            <h2 className="font-display text-[28px] font-bold tracking-[-0.025em] leading-[1.05] mt-2 mb-2">
              {divergenciasCount} lançamento{divergenciasCount !== 1 ? 's' : ''} divergem do extrato
            </h2>
            <p className="font-mono text-sm opacity-80">
              Diferença total: {fmtBRL(diffTotal)}
            </p>
          </Card>
        )}
      </section>

      {parsed ? (
        <>
          {/* Tabela de conciliação */}
          <Card variant="default" className="p-0 overflow-hidden" role="table" aria-label="Tabela de conciliação bancária">
            {/* Cabeçalho */}
            <div className="recon-row bg-card-soft border-b border-card-stroke" role="row">
              <span className="label font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted font-semibold" role="columnheader">
                Extrato banco
              </span>
              <span className="match label font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted font-semibold" role="columnheader">
                Match
              </span>
              <span className="label font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted font-semibold" role="columnheader">
                Lançamento sistema
              </span>
            </div>

            {/* Linhas */}
            <div>
              {sugestoes.length === 0 ? (
                <div className="flex flex-col items-center gap-3 p-10 text-center" role="row">
                  <FileText className="h-8 w-8 text-fg-dim" strokeWidth={1.6} />
                  <p className="text-sm text-fg-muted">Nenhuma transação encontrada no extrato.</p>
                </div>
              ) : (
                sugestoes.map((s, i) => (
                  <ConciliacaoRow
                    key={i}
                    s={s}
                    selected={selectedPagamentos}
                    toggle={togglePagamento}
                  />
                ))
              )}
            </div>
          </Card>

          {/* Rodapé com totais + CTA */}
          <div className="flex flex-col gap-3">
            <p className="font-mono text-xs text-fg-muted text-center">
              Total extrato: <span className="text-fg font-semibold">{fmtBRL(totalBanco)}</span>
              {' · '}
              Sistema: <span className="text-fg font-semibold">{fmtBRL(totalSistema)}</span>
              {' · '}
              Diff: <span className={diffTotal !== 0 ? 'text-warn font-semibold' : 'text-pass font-semibold'}>{fmtBRL(diffTotal)}</span>
            </p>

            <Button
              variant="primary"
              size="lg"
              onClick={applySelected}
              disabled={ctaDisabled}
              aria-disabled={ctaDisabled}
              className="w-full"
            >
              {applyMut.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                  Aplicando…
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" strokeWidth={1.8} />
                  Marcar como conciliado
                </>
              )}
            </Button>

            <div className="text-xs text-fg-muted text-center">
              <button
                type="button"
                onClick={() => setParsed(null)}
                className="text-mint-deep hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint-deep focus-visible:ring-offset-2 rounded-sm"
              >
                Carregar outro extrato
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Dropzone idle */}
          <Card variant="default" className="p-8">
            <Eyebrow className="block mb-3">Importar extrato</Eyebrow>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`flex flex-col items-center justify-center gap-3 rounded-3 border-2 border-dashed px-6 py-12 cursor-pointer transition-colors ${
                dragOver
                  ? 'border-mint-deep bg-mint-soft/40'
                  : 'border-card-stroke bg-card-soft hover:border-mint-deep/60'
              }`}
            >
              <div className="grid h-12 w-12 place-items-center rounded-full bg-mint-soft text-mint-deep">
                {parseMut.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.8} />
                ) : (
                  <Upload className="h-5 w-5" strokeWidth={1.8} />
                )}
              </div>
              <div className="text-center">
                <p className="font-display text-[18px] font-semibold text-fg">
                  {parseMut.isPending ? 'Processando…' : 'Arraste seu extrato OFX'}
                </p>
                <p className="text-sm text-fg-muted mt-1">ou clique pra escolher · até 5 MB</p>
                <p className="flex items-center justify-center gap-1 text-[11px] text-lilac-deep font-medium mt-2">
                  <Sparkles className="h-3 w-3" strokeWidth={2} />
                  Matcher cruza por valor + data ± 2 dias
                </p>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".ofx,application/x-ofx"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
            <p className="text-[11px] text-fg-muted mt-4 text-center">
              OFX · CSV em breve (OCR via Claude Vision).
            </p>
          </Card>
        </>
      )}
    </div>
  );
}

const STATUS_LABEL: Record<MatchSuggestion['status'], string> = {
  matched: 'match',
  centavos: 'centavos',
  ambiguous: 'ambíguo',
  unmatched: 'sem match',
  duplicate_fit: 'duplicado',
};

const STATUS_TONE: Record<MatchSuggestion['status'], 'pass' | 'warn' | 'block' | 'default'> = {
  matched: 'pass',
  centavos: 'warn',
  ambiguous: 'warn',
  unmatched: 'default',
  duplicate_fit: 'block',
};

function ConciliacaoRow({
  s,
  selected,
  toggle,
}: {
  s: MatchSuggestion;
  selected: Set<string>;
  toggle: (id: string) => void;
}) {
  const rowClass =
    s.status === 'centavos' || s.status === 'ambiguous'
      ? 'warn'
      : s.status === 'unmatched' || s.status === 'duplicate_fit'
        ? 'error'
        : 'ok';

  return (
    <div className={`recon-row ${rowClass}`} role="row">
      {/* Coluna esquerda — Extrato */}
      <div role="cell">
        <p className="label">EXTRATO</p>
        <p className="val text-fg">
          {fmtDate(s.transacao.data)} · {fmtBRL(s.transacao.valor)}
        </p>
        <p className="text-[11px] text-fg-muted mt-0.5 truncate">
          {s.transacao.nome ?? s.transacao.descricao ?? '—'}
        </p>
      </div>

      {/* Coluna central — Match */}
      <div className="match" role="cell" aria-label={`Status: ${STATUS_LABEL[s.status]}`}>
        <Pill tone={STATUS_TONE[s.status]} dot className="pill">
          {STATUS_LABEL[s.status]}
        </Pill>
      </div>

      {/* Coluna direita — Candidatos */}
      <div role="cell">
        {s.candidatos.length === 0 ? (
          <>
            <p className="label">SISTEMA</p>
            <p className="text-[11px] text-fg-dim italic">Sem match no sistema</p>
          </>
        ) : (
          <>
            <p className="label">LANÇAMENTO</p>
            <div className="flex flex-col gap-1.5 mt-1">
              {s.candidatos.map((c) => {
                const isSelected = selected.has(c.pagamentoId);
                const checkId = `chk-${c.pagamentoId}`;
                return (
                  <label
                    key={c.pagamentoId}
                    htmlFor={checkId}
                    className={`flex items-center gap-2 cursor-pointer rounded-2 px-2 py-1.5 transition-colors ${
                      isSelected ? 'bg-mint-soft' : 'hover:bg-card-soft/60'
                    }`}
                  >
                    <input
                      id={checkId}
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(c.pagamentoId)}
                      className="h-4 w-4 rounded border-card-stroke accent-mint-deep"
                    />
                    <span className="text-sm font-medium text-fg truncate flex-1">{c.credorNome}</span>
                    <span className="val text-xs">{fmtBRL(c.valor)}</span>
                    <span className="text-[11px] text-fg-muted whitespace-nowrap">
                      {fmtDate(c.dataPagamento)}
                    </span>
                    {c.diff > 0 && (
                      <span className="text-[10px] font-mono text-warn">Δ {fmtBRL(c.diff)}</span>
                    )}
                  </label>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
